import React, { useEffect, useRef } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { directionMap, shapeMap } from '@typings/setting/mouseSet.interface';
import { masterSet } from '@masterSetting/masterSet/masterSetting';

type MouseEffectProps = {
  color: string;
  color2?: string;
  shape?: keyof typeof shapeMap;
  minSize?: number;
  maxSize?: number;
  maxLength?: number;
  direction?: keyof typeof directionMap;
  isActive?: boolean;
  spawnInterval?: number;
};

interface Particle {
  x: number;
  y: number;
  size: number;
  color: string;
  char: string;
  opacity: number;
  velocityY: number;
  velocityX: number;
  life: number;
}

export default function MouseTrail({
  color,
  color2,
  shape = 'heart',
  minSize = 1,
  maxSize = 2,
  maxLength = 30,
  direction = 'goUp',
  isActive = true,
  spawnInterval = 400,
}: MouseEffectProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particles = useRef<Particle[]>([]);
  const mouse = useRef({ x: 0, y: 0 });
  const animationFrame = useRef<number | null>(null);
  const lastSpawnTime = useRef<number>(0);
  const lastMouse = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const colors = [
    ...new TinyColor(color).analogous().map((c) => c.toHexString()),
    ...(color2
      ? new TinyColor(color2).analogous().map((c) => c.toHexString())
      : []),
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive) return;

    const ctx = canvas.getContext('2d')!;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const updateSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', updateSize);

    const spawnParticle = () => {
      const code = shapeMap[shape] ?? 9676;
      const char = String.fromCharCode(code);
      const size = minSize + Math.random() * (maxSize - minSize);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const velocityY = (Math.random() * 0.5 + 0.2) * directionMap[direction];
      const velocityX = (Math.random() - 0.2) * 0.5;

      particles.current.push({
        x: mouse.current.x,
        y: mouse.current.y,
        size,
        color,
        char,
        opacity: 1,
        velocityY,
        velocityX,
        life: 0,
      });

      if (particles.current.length > maxLength) {
        particles.current.shift();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.current = { x: e.clientX, y: e.clientY };

      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // 속도 기반 추가 생성
      const extraCount = Math.min(Math.floor(distance / 10), 2);
      for (let i = 0; i < extraCount; i++) spawnParticle();

      lastMouse.current = { x: e.clientX, y: e.clientY };
    };

    const handleClick = () => {
      spawnParticle();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick);

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const dynamicSpawnInterval = Math.max(1000 / 3 - maxLength * 5, 60);

      if (time - lastSpawnTime.current > dynamicSpawnInterval) {
        const particleCount = 1;
        for (let i = 0; i < particleCount; i++) spawnParticle();
        lastSpawnTime.current = time;
      }

      for (let i = 0; i < particles.current.length; i++) {
        const p = particles.current[i];
        p.x += p.velocityX;
        p.y += p.velocityY;
        p.opacity -= 0.008; // 오래 남게 조절
        p.life++;

        if (p.opacity <= 0) continue; // 👉 opacity 0 이하는 그리지 않음

        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.font = `${p.size}px sans-serif`;
        ctx.fillText(p.char, p.x, p.y);
        ctx.globalAlpha = 1;
      }

      particles.current = particles.current.filter((p) => p.opacity > 0);
      animationFrame.current = requestAnimationFrame(animate);
    };

    animationFrame.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      window.removeEventListener('resize', updateSize);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
    };
  }, [
    color,
    color2,
    shape,
    minSize,
    maxSize,
    maxLength,
    direction,
    isActive,
    spawnInterval,
  ]);

  if (!isActive || !masterSet.cursorCustom) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
}
